
import { supabase } from "@/integrations/supabase/client";

export interface Lesson {
  id: string;
  title: string;
  description: string | null;
  video_url: string | null;
  module_id: string;
  order_index: number;
  module_title?: string;
}

export interface Resource {
  id: string;
  title: string;
  file_url: string;
  description: string | null;
  resource_type: string;
}

export async function getLessonById(id: string) {
  const { data, error } = await supabase
    .from('course_lessons')
    .select(`
      *,
      course_modules(title)
    `)
    .eq('id', id)
    .single();
    
  if (error) throw error;
  
  return {
    ...data,
    module_title: data.course_modules.title,
  } as Lesson;
}

export async function getLessonResources(lessonId: string) {
  const { data, error } = await supabase
    .from('course_resources')
    .select('*')
    .eq('lesson_id', lessonId);
    
  if (error) throw error;
  
  return data as Resource[];
}

export async function getAllLessons() {
  const { data, error } = await supabase
    .from('course_lessons')
    .select('id, title, module_id, order_index, description, video_url')
    .order('module_id')
    .order('order_index');
    
  if (error) throw error;
  
  return data as Lesson[];
}

export async function getUserProgress(userId: string, lessonId: string) {
  const { data, error } = await supabase
    .from('user_progress')
    .select('video_position, completed')
    .eq('user_id', userId)
    .eq('lesson_id', lessonId)
    .single();
    
  if (error && error.code !== 'PGRST116') { // Not Found error
    throw error;
  }
  
  return data;
}

export async function markLessonComplete(userId: string, lessonId: string) {
  const { data, error } = await supabase
    .from('user_progress')
    .upsert({
      user_id: userId,
      lesson_id: lessonId,
      completed: true,
      last_accessed: new Date().toISOString(),
    }, { onConflict: 'user_id,lesson_id' });
    
  if (error) throw error;
  
  return data;
}

export async function isFirstLessonOfFirstModule(lessonId: string) {
  const { data: firstModule } = await supabase
    .from('course_modules')
    .select('id')
    .order('order_index')
    .limit(1)
    .single();
    
  if (firstModule) {
    const { data: firstLesson } = await supabase
      .from('course_lessons')
      .select('id')
      .eq('module_id', firstModule.id)
      .order('order_index')
      .limit(1)
      .single();
      
    return firstLesson && firstLesson.id === lessonId;
  }
  
  return false;
}
